import {
	type NodeViewComponentProps,
	useEditorEventCallback,
	useEditorState,
	useIsNodeSelected,
	useStopEvent,
} from "@handlewithcare/react-prosemirror";
import { HoverCardPortal } from "@radix-ui/react-hover-card";
import cx from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { NodeSelection } from "prosemirror-state";
import {
	type KeyboardEvent,
	type MouseEvent,
	type Ref,
	forwardRef,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { AddIcon, ArrowRightIcon, EditIcon, LinkIcon } from "@moment/icons";
import {
	buildFullSlugPath,
	buildPageLinkText,
	getPageReferenceFragmentCandidates,
	resolvePageReference,
	useCreatePage,
	useDocumentFromCurrentRoute,
	usePageFromCurrentRoute,
	useRouteParams,
} from "@moment/state";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
	cn,
	inlineLinkChipClassName,
} from "@moment/ui";

import { canvasUrlPath } from "~/utils/helpers/url";

import { EditInPlaceInput } from "../../../../navigation-pane/EditInPlaceInput";
import {
	getPageLinkAncestorPath,
	getPageLinkCreateTitle,
	getPageLinkFallbackDisplayTitle,
	getPageLinkResolvedDisplayTitle,
} from "./PageLink.utils";
import { PageLinkPageIcon } from "./PageLinkPageIcon";

export const PageLink = forwardRef<HTMLSpanElement, NodeViewComponentProps>(function PageLink(
	{ nodeProps, children: _, ...props },
	ref
) {
	const { node, getPos } = nodeProps;
	const router = useRouter();

	// Get the page title from the node attributes (this is the link text, may include "Parent/Title")
	const pageTitle = node.attrs["pageTitle"] as string;
	const customDisplayText = (node.attrs["displayText"] as string | null) || null;
	const displayTitle = getPageLinkFallbackDisplayTitle(pageTitle);
	const [aliasValue, setAliasValue] = useState(customDisplayText ?? "");
	const [isEditingAlias, setIsEditingAlias] = useState(false);
	const aliasInputRef = useRef<HTMLInputElement>(null);

	const { documentId, documentMetadata, isEditable } = useDocumentFromCurrentRoute();
	const { pageId: currentPageId } = usePageFromCurrentRoute();
	const { workspaceName } = useRouteParams();
	const createTitle = getPageLinkCreateTitle(
		pageTitle,
		documentMetadata?.pagesMetadata ?? [],
		currentPageId
	);

	// In read-only mode, PageLinks act as simple links without popovers
	const isReadOnly = !isEditable;

	// Resolve the page by title lookup
	const resolvedReference = useMemo(() => {
		if (!documentMetadata?.pagesMetadata) {
			return null;
		}
		return resolvePageReference(pageTitle, documentMetadata.pagesMetadata, currentPageId);
	}, [pageTitle, documentMetadata?.pagesMetadata, currentPageId]);
	const createdPageFragment = useMemo(() => {
		if (resolvedReference?.fragment) {
			return resolvedReference.fragment;
		}

		const [candidate] = getPageReferenceFragmentCandidates(pageTitle);
		if (!candidate) {
			return undefined;
		}

		return getPageLinkFallbackDisplayTitle(candidate.basePageRef) === createTitle
			? candidate.fragment
			: undefined;
	}, [resolvedReference?.fragment, pageTitle, createTitle]);
	const page = resolvedReference?.page ?? null;

	const isPageFound = !!page;
	const resolvedPageTitle = page?.title || "Untitled";
	const resolvedDisplayTitle = page
		? getPageLinkResolvedDisplayTitle(resolvedPageTitle, resolvedReference?.fragment)
		: displayTitle;

	// Build the full slug path for navigation
	const fullSlugPath = useMemo(() => {
		if (!page || !documentMetadata?.pagesMetadata) {
			return null;
		}
		return buildFullSlugPath(documentMetadata.pagesMetadata, page);
	}, [page, documentMetadata?.pagesMetadata]);

	// Build the full ancestor chain for display in the popover (e.g., "Grandparent / Parent")
	const ancestorPath = useMemo(
		() => getPageLinkAncestorPath(page, documentMetadata?.pagesMetadata),
		[page, documentMetadata?.pagesMetadata]
	);

	// Build the navigation URL
	const pageUrl = useMemo(() => {
		if (!page || !workspaceName || !documentId) {
			return null;
		}
		const basePageUrl = canvasUrlPath({
			workspaceName,
			pageSlug: fullSlugPath ?? undefined,
			pageId: page.id,
			version: "",
			type: "CanvasById",
			id: documentId,
		});
		return resolvedReference?.fragment
			? `${basePageUrl}#${resolvedReference.fragment}`
			: basePageUrl;
	}, [page, fullSlugPath, workspaceName, documentId, resolvedReference?.fragment]);

	const isNodeSelected = useIsNodeSelected();
	const editorState = useEditorState();
	const [isHoverCardOpen, setIsHoverCardOpen] = useState(false);

	// Check if the node is within a text selection
	// getPos() can return undefined if the node has been deleted
	const pos = getPos();
	const { from, to } = editorState.selection;
	const isInTextSelection = typeof pos === "number" && from < pos + 1 && to > pos;

	const isSelected = isNodeSelected || isInTextSelection;

	useEffect(() => {
		setIsHoverCardOpen(isNodeSelected);
	}, [isNodeSelected]);

	useEffect(() => {
		setAliasValue(customDisplayText ?? "");
	}, [customDisplayText]);

	useEffect(() => {
		if (!isEditingAlias) {
			return;
		}
		aliasInputRef.current?.focus();
		aliasInputRef.current?.select();
	}, [isEditingAlias]);

	const onClick = useEditorEventCallback((view) => {
		const pos = getPos();
		// getPos() can return undefined if the node has been deleted
		if (pos === undefined) {
			return;
		}
		view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, pos)));
	});

	const handleClick = (event: MouseEvent) => {
		// Ctrl/Cmd+click navigates directly to the page
		if ((event.ctrlKey || event.metaKey) && pageUrl) {
			router.push(pageUrl);
			return;
		}
		onClick();
	};

	useStopEvent((_view, event) => {
		// Prevent drag events on the page link node
		if (event instanceof DragEvent) {
			event.preventDefault();
			return true;
		}
		return false;
	});

	const handleOpenPage = (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		if (pageUrl) {
			router.push(pageUrl);
		}
	};

	// Hooks must be called before any early returns
	const createPage = useCreatePage();

	const updatePageLinkTitle = useEditorEventCallback((view, newTitle: string) => {
		const currentPos = getPos();
		// getPos() can return undefined if the node has been deleted
		if (currentPos === undefined) {
			return;
		}
		const tr = view.state.tr.setNodeMarkup(currentPos, undefined, {
			...node.attrs,
			pageTitle: newTitle,
		});
		view.dispatch(tr);
	});

	const updatePageLinkDisplayText = useEditorEventCallback(
		(view, newDisplayText: string | null) => {
			const currentPos = getPos();
			if (currentPos === undefined) {
				return;
			}
			const tr = view.state.tr.setNodeMarkup(currentPos, undefined, {
				...node.attrs,
				displayText: newDisplayText,
			});
			view.dispatch(tr);
		}
	);

	const commitAliasValue = () => {
		const nextDisplayText = aliasValue.length > 0 ? aliasValue : null;
		if (nextDisplayText === customDisplayText) {
			setIsEditingAlias(false);
			return;
		}
		updatePageLinkDisplayText(nextDisplayText);
		setIsEditingAlias(false);
	};

	const handleAliasKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			commitAliasValue();
			return;
		}

		if (event.key === "Escape") {
			event.preventDefault();
			setAliasValue(customDisplayText ?? "");
			setIsEditingAlias(false);
		}
	};

	const showAliasEditor = (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setAliasValue(customDisplayText ?? "");
		setIsEditingAlias(true);
	};

	const handleCreatePage = async () => {
		if (!documentId || !documentMetadata || !workspaceName) {
			return;
		}

		// Create a new page with the display title from the broken link
		const newPage = await createPage({
			documentId,
			pageIndex: documentMetadata.pagesMetadata.length,
			pageType: "markdown",
			pageMetadata: { title: createTitle },
		});

		if (newPage) {
			// Use buildPageLinkText to properly escape slashes in the title.
			// Without this, a title like "A/B" would be stored as-is and
			// resolvePageFromLinkText would misinterpret it as parent="A", child="B".
			const linkText = buildPageLinkText(newPage.id, [
				...documentMetadata.pagesMetadata,
				newPage,
			]);
			const nextPageLinkTitle = createdPageFragment
				? `${linkText}#${createdPageFragment}`
				: linkText;
			updatePageLinkTitle(nextPageLinkTitle || displayTitle);

			const newSlugPath = buildFullSlugPath(
				[...documentMetadata.pagesMetadata, newPage],
				newPage
			);
			router.push(
				canvasUrlPath({
					workspaceName,
					pageSlug: newSlugPath,
					pageId: newPage.id,
					version: "",
					type: "CanvasById",
					id: documentId,
				})
			);
		}
	};

	// In read-only mode, render a normal Next.js link so published web pages route correctly.
	if (isReadOnly) {
		const className = cx(
			inlineLinkChipClassName({
				interactive: isPageFound,
				selected: isSelected,
				tone: isPageFound ? "default" : "negative",
			}),
			"[&::selection]:!bg-transparent",
			"[&::selection]:![color:currentColor]",
			"[&_*::selection]:!bg-transparent",
			"[&_*::selection]:![color:currentColor]"
		);

		const content = (
			<>
				{isPageFound ? (
					<PageLinkPageIcon
						documentId={documentId}
						page={page}
						className="w-3 h-3 text-secondary"
					/>
				) : (
					<LinkIcon className="w-3 h-3 text-secondary" />
				)}
				<span
					className={cn(
						"text-sm font-normal",
						isPageFound ? "text-primary" : "text-palette-negative-11 line-through"
					)}
				>
					{customDisplayText ?? resolvedDisplayTitle}
				</span>
			</>
		);

		if (isPageFound && pageUrl) {
			return (
				<Link
					ref={ref as Ref<HTMLAnchorElement>}
					contentEditable={false}
					suppressContentEditableWarning
					{...props}
					href={pageUrl}
					prefetch={false}
					className={className}
				>
					{content}
				</Link>
			);
		}

		return (
			<span
				ref={ref}
				contentEditable={false}
				suppressContentEditableWarning
				{...props}
				className={className}
			>
				{content}
			</span>
		);
	}

	return (
		<HoverCard openDelay={200} open={isHoverCardOpen} onOpenChange={setIsHoverCardOpen}>
			<HoverCardTrigger asChild>
				<span
					ref={ref}
					contentEditable={false}
					suppressContentEditableWarning
					{...props}
					className={cx(
						inlineLinkChipClassName({
							interactive: true,
							selected: isSelected,
							tone: isPageFound ? "default" : "negative",
						}),
						// Prevent selection background from showing inside the node view
						"[&::selection]:!bg-transparent",
						"[&::selection]:![color:currentColor]",
						"[&_*::selection]:!bg-transparent",
						"[&_*::selection]:![color:currentColor]"
					)}
					onClick={handleClick}
				>
					{isPageFound ? (
						<PageLinkPageIcon
							documentId={documentId}
							page={page}
							className="w-3 h-3 text-secondary"
						/>
					) : (
						<LinkIcon className="w-3 h-3 text-secondary" />
					)}
					<span
						className={cn(
							"text-sm font-normal",
							isPageFound ? "text-primary" : "text-palette-negative-11 line-through"
						)}
					>
						{customDisplayText ?? resolvedDisplayTitle}
					</span>
				</span>
			</HoverCardTrigger>
			<HoverCardPortal>
				<HoverCardContent className="p-1" side="top" sideOffset={4}>
					<div className="flex items-center gap-2">
						<div className="min-w-0 flex-1 px-2">
							{isPageFound ? (
								<div className="min-w-0">
									<div className="text-xs text-secondary">
										{ancestorPath && (
											<span className="font-normal text-secondary">
												{ancestorPath}
												{" / "}
											</span>
										)}
										<span className="font-bold text-primary">
											{resolvedDisplayTitle}
										</span>
									</div>
									{(customDisplayText || isEditingAlias) && (
										<div className="mt-0.5 text-xs text-secondary">
											<span className="mr-1 uppercase tracking-wide text-[10px]">
												Alias
											</span>
											{isEditingAlias ? (
												<EditInPlaceInput
													ref={aliasInputRef}
													value={aliasValue}
													placeholder={resolvedDisplayTitle}
													onChange={(event) =>
														setAliasValue(event.currentTarget.value)
													}
													onBlur={commitAliasValue}
													onKeyDown={handleAliasKeyDown}
													autoSize
													textSize="sm"
													className="inline-block align-baseline border-b-2 border-transparent focus:border-brand"
												/>
											) : (
												<span className="text-primary">
													{customDisplayText}
												</span>
											)}
										</div>
									)}
								</div>
							) : (
								<div className="min-w-0">
									<div className="text-xs text-secondary">
										<span className="text-palette-negative-11">
											{displayTitle}
										</span>
									</div>
									{(customDisplayText || isEditingAlias) && (
										<div className="mt-0.5 text-xs text-secondary">
											<span className="mr-1 uppercase tracking-wide text-[10px]">
												Alias
											</span>
											{isEditingAlias ? (
												<EditInPlaceInput
													ref={aliasInputRef}
													value={aliasValue}
													placeholder={displayTitle}
													onChange={(event) =>
														setAliasValue(event.currentTarget.value)
													}
													onBlur={commitAliasValue}
													onKeyDown={handleAliasKeyDown}
													autoSize
													textSize="sm"
													className="inline-block align-baseline border-b-2 border-transparent text-palette-negative-11 focus:border-brand"
												/>
											) : (
												<span className="text-palette-negative-11">
													{customDisplayText}
												</span>
											)}
										</div>
									)}
								</div>
							)}
						</div>
						<button
							type="button"
							onClick={showAliasEditor}
							className="inline-flex items-center justify-center rounded p-1 text-secondary transition-colors hover:bg-palette-gray-4 hover:text-primary"
							aria-label={customDisplayText ? "Edit link alias" : "Add link alias"}
						>
							<EditIcon className="size-3" />
						</button>
						{isPageFound ? (
							<button
								type="button"
								onClick={handleOpenPage}
								disabled={!pageUrl}
								className={cn(
									"inline-flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors",
									pageUrl
										? "bg-palette-gray-4 hover:bg-palette-gray-5 text-primary"
										: "bg-palette-gray-3 text-secondary cursor-not-allowed"
								)}
							>
								Open
								<ArrowRightIcon className="w-3 h-3" />
							</button>
						) : (
							<button
								type="button"
								onClick={handleCreatePage}
								disabled={!documentId}
								className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors bg-palette-gray-4 hover:bg-palette-gray-5 text-primary"
							>
								Create
								<AddIcon className="w-3 h-3" />
							</button>
						)}
					</div>
				</HoverCardContent>
			</HoverCardPortal>
		</HoverCard>
	);
});
